/**
 * Company Page Tabs UI
 * Borrowed from https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/js/tabs.js
 *
 * @module
 */
/* eslint-disable
	no-undef,
	no-shadow,
	no-lonely-if,
	no-unused-expressions,
	no-unused-vars
*/

export class Tabs {
	/**
	 * Tabs component that converts to an Accordion on mobile.
	 *
	 * @param {string} [isEnabledMediaQuery] Media query specifying when the accordion should be enabled.
	 */
	constructor( isEnabledMediaQuery ) {
		this.isEnabledMediaQuery = isEnabledMediaQuery;
		this.keys = {
			end: 35,
			home: 36,
			left: 37,
			up: 38,
			right: 39,
			down: 40,
			delete: 46,
		};
		this.direction = {
			37: -1,
			38: -1,
			39: 1,
			40: 1,
		};
	}

	/**
	 * Initialize Tabs UI.
	 *
	 * @return {void}
	 */
	init() {
		const tablists = document.querySelectorAll(
			'#ticker-main [role="tablist"]'
		);

		if ( tablists.length === 0 ) {
			return;
		}

		this.tablist = tablists[ 0 ];

		this.delay = this.determineDelay();

		this.generateArrays();

		// Bind listeners
		let i;
		for ( i = 0; i < this.tabs.length; ++i ) {
			this.addListeners( i );
		}

		const tabToActivate = this.getTabFromURL();

		this.initStatusMediaQuery();

		if ( tabToActivate ) {
			this.activateTheTab( tabToActivate );
			this.focusActiveTab( tabToActivate );
		} else {
			// check for user interactivity on mobile
			if ( this.tablist.classList.contains( 'is-accordion' ) ) {
				document.addEventListener(
					'touchstart',
					( e ) => {
						this.activateTheTab( this.tabs[ 0 ] );
					},
					{ once: true }
				);
			} else {
				// just activate the tab for desktops
				this.activateTheTab( this.tabs[ 0 ] );
			}
		}

		this.popped = false;

		window.onpopstate = function () {
			const tabToActivate = this.getTabFromURL();

			if ( tabToActivate ) {
				this.popped = true;
				this.activateTheTab( tabToActivate, true );
			}
		}.bind( this );
	}

	initStatusMediaQuery() {
		if (
			! this.isEnabledMediaQuery ||
			! (
				'MediaQueryList' in window &&
				'addEventListener' in MediaQueryList.prototype
			)
		) {
			return;
		}
		const mql = window.matchMedia( this.isEnabledMediaQuery );
		mql.addEventListener( 'change', ( e ) => {
			e.matches ? this.enableAccordion() : this.disableAccordion();
		} );
		mql.matches ? this.enableAccordion() : this.disableAccordion();
	}

	determineDelay() {
		const hasDelay = this.tablist.hasAttribute( 'data-delay' );
		let delay = 0;

		if ( hasDelay ) {
			const delayValue = this.tablist.getAttribute( 'data-delay' );
			if ( delayValue ) {
				delay = delayValue;
			} else {
				// If no value is specified, default to 300ms
				delay = 300;
			}
		}

		return delay;
	}

	generateArrays() {
		this.tabs = document.querySelectorAll( '[role="tab"]' );
		this.panels = document.querySelectorAll( '[role="tabpanel"]' );
	}

	addListeners( index ) {
		this.tabs[ index ].addEventListener( 'click', ( e ) => {
			this.clickEventListener( e );
		} );
		this.tabs[ index ].addEventListener( 'keydown', ( e ) => {
			this.keydownEventListener( e );
		} );
		this.tabs[ index ].addEventListener( 'keyup', ( e ) => {
			this.keyupEventListener( e );
		} );

		// Build an array with all tabs (<button>s) in it
		this.tabs[ index ].index = index;
	}

	getTabFromURL() {
		if ( ! location.hash ) {
			return null;
		}

		const hash = location.hash;

		const tab = document.querySelector( hash );
		const foundElement = Array.from( this.tabs ).find(
			( node ) => node === tab
		);
		if ( hash && foundElement ) {
			return tab;
		}

		return null;
	}

	clickEventListener( event ) {
		event.preventDefault();
		const tab = event.target;
		// reset on click
		this.popped = false;
		this.activateTheTab( tab );
		this.focusActiveTab( tab );
	}

	keydownEventListener( event ) {
		const key = event.keyCode;

		switch ( key ) {
			case this.keys.end:
				event.preventDefault();
				// Activate last tab
				this.activateTheTab( this.tabs[ this.tabs.length - 1 ] );
				break;
			case this.keys.home:
				event.preventDefault();
				// Activate first tab
				this.activateTheTab( this.tabs[ 0 ] );
				break;

			// Up and down are in keydown
			// because we need to prevent page scroll >:)
			case this.keys.up:
			case this.keys.down:
				this.determineOrientation( event );
				break;
		}
	}

	keyupEventListener( event ) {
		const key = event.keyCode;

		switch ( key ) {
			case this.keys.left:
			case this.keys.right:
				this.determineOrientation( event );
				break;
			case this.keys.delete:
				this.determineDeletable( event );
				break;
		}
	}

	activateTheTab( tab ) {
		// Deactivate all other tabs
		const prevTab =
			this.tablist.querySelector( '[aria-expanded="true"]' ) ?? tab;
		this.deactivateTabs();

		// Remove tabindex attribute
		tab.removeAttribute( 'tabindex' );

		// Set the tab as selected
		tab.setAttribute( 'aria-selected', 'true' );

		// Get the value of aria-controls (which is an ID)
		const controls = tab.getAttribute( 'aria-controls' );

		// Regenerate QMod tool content
		const panel = document.getElementById( controls );
		const qmodTool = panel.dataset.qmodToolname;
		if ( qmodTool && window.qMod ) {
			const qtoolDiv = panel.querySelector( '.qtool' );
			qtoolDiv.dataset.qmodTool = qmodTool;
			qtoolDiv.id = qmodTool + '_widget';
			window.qMod.loadTool( qtoolDiv.id );
		}

		if ( this.tablist.classList.contains( 'is-accordion' ) ) {
			const elem = document.getElementById( controls );
			elem.classList.add( 'moved' );
			this.tablist.insertBefore( elem, tab.nextSibling );
		}

		// Toggle panel visibility for accordions
		if (
			this.tablist.classList.contains( 'is-accordion' ) &&
			tab.id === prevTab.id
		) {
			if ( tab.getAttribute( 'aria-expanded' ) === 'true' ) {
				document
					.getElementById( controls )
					.setAttribute( 'hidden', 'hidden' );
				tab.setAttribute( 'aria-selected', 'false' );
				tab.setAttribute( 'aria-expanded', 'false' );
			} else {
				document.getElementById( controls ).removeAttribute( 'hidden' );
				tab.setAttribute( 'aria-expanded', 'true' );
			}
		} else {
			document.getElementById( controls ).removeAttribute( 'hidden' );
			tab.setAttribute( 'aria-expanded', 'true' );
			prevTab.setAttribute( 'aria-expanded', 'false' );
		}

		// adjust chart sizes when viewport resizes
		window.dispatchEvent( new Event( 'resize' ) );
	}

	focusActiveTab( tab ) {
		if ( this.tablist.classList.contains( 'is-accordion' ) ) {
			tab.scrollIntoView( {
				behavior: 'smooth',
				block: 'start',
				inline: 'center',
			} );
		}

		tab.focus();
		if ( ! this.popped ) {
			if ( history.pushState ) {
				history.pushState( null, null, '#' + tab.id );
			} else {
				window.location.hash = tab.id;
			}
		}
	}

	deactivateTabs() {
		let t;
		for ( t = 0; t < this.tabs.length; t++ ) {
			this.tabs[ t ].setAttribute( 'tabindex', '-1' );
			this.tabs[ t ].setAttribute( 'aria-selected', 'false' );
			this.tabs[ t ].removeEventListener(
				'focus',
				this.focusEventHandler
			);
		}

		let p;
		for ( p = 0; p < this.panels.length; p++ ) {
			this.panels[ p ].setAttribute( 'hidden', 'hidden' );
		}
	}

	determineOrientation( event ) {
		const key = event.keyCode;
		const vertical =
			this.tablist.getAttribute( 'aria-orientation' ) === 'vertical';
		let proceed = false;

		if ( vertical ) {
			if ( key === this.keys.up || key === this.keys.down ) {
				event.preventDefault();
				proceed = true;
			}
		} else if ( key === this.keys.left || key === this.keys.right ) {
			proceed = true;
		}

		if ( proceed ) {
			this.switchTabOnArrowPress( event );
		}
	}

	switchTabOnArrowPress( event ) {
		const pressed = event.keyCode;

		let x;
		for ( x = 0; x < this.tabs.length; x++ ) {
			this.tabs[ x ].addEventListener( 'focus', this.focusEventHandler );
		}

		if ( this.direction[ pressed ] ) {
			const target = event.target;
			if ( target.index !== undefined ) {
				if ( this.tabs[ target.index + this.direction[ pressed ] ] ) {
					this.tabs[
						target.index + this.direction[ pressed ]
					].focus();
				} else if (
					pressed === this.keys.left ||
					pressed === this.keys.up
				) {
					this.focusLastTab();
				} else if (
					pressed === this.keys.right ||
					pressed === this.keys.down
				) {
					this.focusFirstTab();
				}
			}
		}
	}

	focusEventHandler( event ) {
		const target = event.target;

		setTimeout( this.checkTabFocus, this.delay, target );
	}

	focusFirstTab() {
		this.tabs[ 0 ].focus();
	}

	focusLastTab() {
		this.tabs[ this.tabs.length - 1 ].focus();
	}

	checkTabFocus( target ) {
		const focused = document.activeElement;

		if ( target === focused ) {
			this.activateTheTab( target );
		}
	}

	determineDeletable( event ) {
		const target = event.target;

		if ( target.getAttribute( 'data-deletable' ) !== null ) {
			// Delete target tab
			this.deleteTab( event, target );

			// Update arrays related to tabs widget
			this.generateArrays();

			// Activate the closest tab to the one that was just deleted
			if ( target.index - 1 < 0 ) {
				this.activateTheTab( this.tabs[ 0 ] );
			} else {
				this.activateTheTab( this.tabs[ target.index - 1 ] );
			}
		}
	}

	deleteTab( event ) {
		const target = event.target;
		const panel = document.getElementById(
			target.getAttribute( 'aria-controls' )
		);

		target.parentElement.removeChild( target );
		panel.parentElement.removeChild( panel );
	}

	enableAccordion() {
		this.tablist.classList.add( 'is-accordion' );
		this.tabs.forEach( function ( button ) {
			button.setAttribute( 'aria-expanded', 'false' );
		} );
	}

	disableAccordion() {
		const moved = document.querySelectorAll( '.is-accordion .moved' );
		let i;
		for ( i = 0; i < moved.length; ++i ) {
			moved[ i ].classList.remove( 'moved' );
			this.tablist.parentNode.appendChild( moved[ i ] );
		}
		this.tabs.forEach( function ( button ) {
			button.removeAttribute( 'aria-expanded' );
		} );
		this.tablist.classList.remove( 'is-accordion' );
	}
}
